<template>
  <a-modal
    v-model="showModal"
    :title="isActiveProgram ? 'Stop the referral program' : 'Start the referral program'"
    :footer="null"
    width="600px"
    :destroyOnClose="true"
    class="change-status-modal"
  >
    <div v-if="isActiveProgram" class="mb-3">
      <div v-if="isBeforeStartDate">
        <span class="d-block mb-2 font-weight-bold">You can safely stop the program now.</span>
        <ul class="pl-4">
          <li>Once stopped, all fields will become editable again.</li>
          <li>You’ll also be able to restart the program.</li>
        </ul>
        <span class="font-weight-bold">Do you want to stop the program now?</span>
      </div>
      <div v-else>
        <span class="d-block mb-2">Stopping the program will:</span>
        <ul class="pl-4">
          <li>Immediately <span class="font-weight-bold">stop all calculations</span></li>
          <li><span class="font-weight-bold">Permanently deactivate</span> the program — it <span class="font-weight-bold">cannot be restarted</span>. This action is irreversible.</li>
        </ul>
        <span class="font-weight-bold">Are you sure you want to continue?</span>
      </div>
    </div>
    <div v-else class="mb-3">
      <span class="d-block mb-2 font-weight-bold">Important:</span>
      <ul class="pl-4">
        <li>You can fully edit all fields <span class="font-weight-bold">only while the program is inactive</span>.</li>
        <li>Once the program is active, editing is only possible <span class="font-weight-bold">after stopping the program, but only if the start date has not yet begun</span>.</li>
        <li>After the start date begins, <span class="font-weight-bold">editing is no longer possible</span>.</li>
        <li>Stopping the program after the start date is permanent — it <span class="font-weight-bold">cannot be restarted</span>.</li>
      </ul>
      <span class="font-weight-bold">Are you sure you want to proceed?</span>
    </div>
    <a-row class="footer-viewscreen footer d-flex justify-content-end">
      <a-button
        :loading="loading"
        @click="closeModal"
      >
        Cancel
      </a-button>
      <a-button
        :type="isActiveProgram ? 'danger' : 'primary'"
        class="ml-2"
        :loading="loading"
        @click="changeStatus"
      >
        {{ isActiveProgram ? 'Stop' : 'Start' }}
      </a-button>
    </a-row>
  </a-modal>
</template>

<script>
import apiClient from '@/services/axios'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    program: {
      type: Object,
      default: null,
    },
    isActiveProgram: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showModal: false,
    loading: false,
  }),
  watch: {
    show: {
      handler(val) {
        if (val) {
          this.showModal = true
        } else {
          this.showModal = false
        }
      },
      deep: true,
      immediate: true,
    },
    showModal: {
      handler(val) {
        if (!val) {
          this.closeModal()
        }
      },
      deep: true,
    },
  },
  computed: {
    isBeforeStartDate() {
      const now = this.$moment()
      const start = this.$moment(this.program.started_at)
      if (now.isSameOrBefore(start)) {
        return true
      }
      return false
    },
  },
  methods: {
    async changeStatus() {
      try {
        this.loading = true
        const programId = this.$route.params.id

        const url = `${referralApiUrl}/referral-programs/${programId}/status`
        const response = await apiClient.post(url, { status: this.isActiveProgram ? 'inactive' : 'active' })
        const { data } = response?.data
        if (data) {
          this.$notification.success({
            message: this.isActiveProgram ? 'Referral program successfully stopped' : 'Referral program successfully started',
          })
          const status = data?.status ? data.status : this.isActiveProgram ? 'inactive' : 'active'
          this.$emit('updateStatus', status)
          this.closeModal()
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    closeModal() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss">
.change-status-modal {
  .footer {
    display: flex;
    justify-content: space-between;
    &::before,
    &::after {
      content: none;
    }
  }
}
</style>
